import { useCallback } from 'react';
import { i18n } from '@lingui/core';

import {
  useOrionToLumiaConverterContract,
  useProcessContractError,
} from '../../shared/contracts';
import { useProviderOrSigner } from '../../shared/hooks';
import { useAddTxToQueue } from '../../shared/redux/actionCreators';
import { Contracts } from '../../shared/types';
import { useAppChainId } from '../../shared/api';

export const orionToLumiaConverterContractAddress = {
  56: '0x7c2eb61ca9FB89844b10f62dF23490E43FFfE913',
  1: '0xFBaa4E673D0cD1159beD704Fdc0e4379a41c2135',
  97: '0xbd93f9969dfd944aC5fdDe2b90956051A4f3f526'
};

export type ConvertConfig = {
  onSuccess?: () => void;
  onError?: (error?: Error) => void;
  onFail?: () => void;
};

export function useOrionToLumiaConverter(): {
  convertOrionToLumia: (
    ornAmount: number,
    config?: ConvertConfig,
  ) => Promise<void>;
} {
  const handleContractError = useProcessContractError();
  const chainId = useAppChainId();

  const { signer } = useProviderOrSigner();
  const orionToLumiaConverterContract = useOrionToLumiaConverterContract(
    orionToLumiaConverterContractAddress[chainId],
    signer,
  );

  const addTxToQueue = useAddTxToQueue();

  const convertOrionToLumia = useCallback(
    async (
      ornAmount: number,
      { onSuccess, onError, onFail }: ConvertConfig = {},
    ): Promise<void> => {
      if (!orionToLumiaConverterContract) {
        return;
      }

      await orionToLumiaConverterContract.convert
        .populateTransaction(ornAmount)
        .then((unsignedTx) => {
          addTxToQueue({
            unsignedTx,
            summary: i18n._('Swap ORN to LUMIA'),
            onTxSuccess: () => {
              onSuccess?.();
            },
            onTxError: ({ error }) => {
              onError?.(error);

              handleContractError(error, Contracts.OrionToLumiaConverter);
            },
          });
        })
        .catch((e) => console.log(e));
    },
    [orionToLumiaConverterContract, addTxToQueue, handleContractError],
  );

  return {
    convertOrionToLumia,
  };
}
